import { t } from '@lingui/core/macro';

import HashIcon from '@/assets/svg/bx-hash.svg?react';
import PercentIcon from '@/assets/svg/bx-percent.svg?react';
import { Button, type ButtonProps } from '@/components/buttons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip';

type TooltipPlacement =
  | 'top'
  | 'topLeft'
  | 'topRight'
  | 'bottom'
  | 'bottomLeft'
  | 'bottomRight'
  | 'left'
  | 'leftTop'
  | 'leftBottom'
  | 'right'
  | 'rightTop'
  | 'rightBottom';

const getTooltipPlacementProps = (placement: TooltipPlacement) => {
  switch (placement) {
    case 'topLeft':
      return { side: 'top' as const, align: 'start' as const };
    case 'top':
      return { side: 'top' as const, align: 'center' as const };
    case 'topRight':
      return { side: 'top' as const, align: 'end' as const };
    case 'bottomLeft':
      return { side: 'bottom' as const, align: 'start' as const };
    case 'bottom':
      return { side: 'bottom' as const, align: 'center' as const };
    case 'bottomRight':
      return { side: 'bottom' as const, align: 'end' as const };
    case 'leftTop':
      return { side: 'left' as const, align: 'start' as const };
    case 'left':
      return { side: 'left' as const, align: 'center' as const };
    case 'leftBottom':
      return { side: 'left' as const, align: 'end' as const };
    case 'rightTop':
      return { side: 'right' as const, align: 'start' as const };
    case 'right':
      return { side: 'right' as const, align: 'center' as const };
    case 'rightBottom':
      return { side: 'right' as const, align: 'end' as const };
    default:
      return { side: 'top' as const, align: 'center' as const };
  }
};

type Props = {
  mode: 'stack' | 'percent';
  size?: ButtonProps['size'];
  tooltipPlacement?: TooltipPlacement;
  onChange: (mode: 'stack' | 'percent') => void;
};

const StackModeToggleButton = ({
  mode,
  size = 'medium',
  tooltipPlacement = 'top',
  onChange,
}: Props) => {
  const placement = getTooltipPlacementProps(tooltipPlacement);
  let buttonSize: 'icon-small' | 'icon' | 'icon-large' = 'icon';
  if (size === 'small') {
    buttonSize = 'icon-small';
  } else if (size === 'large') {
    buttonSize = 'icon-large';
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size={buttonSize}
            onClick={() => onChange(mode === 'stack' ? 'percent' : 'stack')}
          >
            {mode === 'stack' ? <PercentIcon /> : <HashIcon />}
          </Button>
        }
      />
      <TooltipContent side={placement.side} align={placement.align}>
        {mode === 'stack' ? t`Show percent of total` : t`Show stacked values`}
      </TooltipContent>
    </Tooltip>
  );
};

export default StackModeToggleButton;
